import { type CalendarData } from '@/services/api'
import styles from './MonthlyCalendar.module.css'

interface Props {
  data: CalendarData | null
  currentMonth: string
  onMonthChange: (month: string) => void
}

export default function MonthlyCalendar({ data, currentMonth, onMonthChange }: Props) {
  const [year, month] = currentMonth.split('-').map(Number)
  const today = new Date().toISOString().split('T')[0]

  const handlePrev = () => {
    const d = new Date(year, month - 2, 1)
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  const handleNext = () => {
    const d = new Date(year, month, 1)
    const now = new Date()
    if (d <= now) {
      onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    }
  }

  const monthLabel = new Date(year, month - 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  })

  // Calculate grid: first day of month (0=Sun, adjust to Mon=0)
  const firstDay = new Date(year, month - 1, 1).getDay()
  const startOffset = firstDay === 0 ? 6 : firstDay - 1

  const activeDates = new Set(
    (data?.days || []).filter(d => d.count > 0).map(d => d.date)
  )

  const daysInMonth = new Date(year, month, 0).getDate()

  const isNextDisabled = () => {
    const nextMonth = new Date(year, month, 1)
    return nextMonth > new Date()
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.navBtn} onClick={handlePrev}>&lt;</button>
        <h3 className={styles.monthLabel}>{monthLabel}</h3>
        <button
          className={styles.navBtn}
          onClick={handleNext}
          disabled={isNextDisabled()}
        >
          &gt;
        </button>
      </div>

      <div className={styles.weekdays}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className={styles.weekday}>{d}</span>
        ))}
      </div>

      <div className={styles.grid}>
        {Array.from({ length: startOffset }).map((_, i) => (
          <div key={`empty-${i}`} className={styles.cell} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
          const isActive = activeDates.has(dateStr)
          const isToday = dateStr === today

          return (
            <div
              key={dayNum}
              className={`${styles.cell} ${isToday ? styles.today : ''}`}
            >
              <span className={styles.dayNum}>{dayNum}</span>
              {isActive && <span className={styles.dot} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
